import React, { Fragment, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { /*useNavigate,*/ useSearchParams } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { Trans, useTranslation } from 'react-i18next';
import * as API from 'api/baseAPI';
import logo from 'images/logo.png';
import mobileWebBanner from 'images/iomed-banner.png';
import webBanner from 'images/iomed-banner.png';
import eventLogo from 'images/event-logo.png';
import beianLogo from 'images/beian.png';
import { getMultiLocaleContent } from 'utils/helper';

import CircularProgress from '@mui/material/CircularProgress';

import LocaleSelector from 'components/localeSelector';
import { DialogContext } from 'components/dialogContext';
import COUNTRIES, { CHINA_GROUP } from 'data/countries';

import 'styles/scss/components/registrationPage.scss';
import { useMatchMedia } from './useMatchMedia';

export default function RegistrationPage(props) {
  // const navigate = useNavigate();
  const { eventId } = props;
  const [searchParams] = useSearchParams();
  const customization = useSelector((state) => state.event.customization);
  const [attendanceType, setAttendanceType] = useState('online');
  const [attendanceTypeError, setAttendanceTypeError] = useState(null);
  const [title, setTitle] = useState('');
  const [titleError, setTitleError] = useState(null);
  const [email, setEmail] = useState('');
  const [emailError, setEmailError] = useState(null);
  const [firstName, setFirstName] = useState('');
  const [firstNameError, setFirstNameError] = useState(null);
  const [lastName, setLastName] = useState('');
  const [lastNameError, setLastNameError] = useState(null);
  const [company, setCompany] = useState('');
  const [companyError, setCompanyError] = useState(null);
  const [jobTitle, setJobTitle] = useState('');
  const [countryRegion, setCountryRegion] = useState('hong-kong');
  const [countryRegionError, setCountryRegionError] = useState(null);
  const [acceptPics, setAcceptPics] = useState(false);
  const [acceptPicsError, setAcceptPicsError] = useState(null);
  const [acceptPhoto, setAcceptPhoto] = useState(false);
  const [acceptPhotoError, setAcceptPhotoError] = useState(null);
  const [acceptTerms, setAcceptTerms] = useState(false);
  const [acceptTermsError, setAcceptTermsError] = useState(null);
  const [acceptMarketing, setAcceptMarketing] = useState(false);
  const { t, i18n } = useTranslation();
  const [loading, setLoading] = useState(false);
  const [submittable, setSubmittable] = useState(true);
  const [customStyle, setCustomStyle] = useState({});
  const [customConfig, setCustomConfig] = useState({
    enableLocale: false,
    availableLocales: ['en', 'zh-Hant', 'zh-Hans'],
    metadata: [],
    logoImageUrl: null,
    logoImageUrlMobile: null,
    logoInHeader: false,
    footerImageUrl: null,
    footerText: null,
    footerLink: null,
    hideFooter: false,
    hideFooterLink: false,
    mobileBanner: mobileWebBanner,
    mobileBottomBanner: null,
    label: null,
    title: null,
    submitButtonText: null,
    remarks: [],
    ignoreCase: false,
    autoCapitalize: false,
    errorMessage: null,
    hideInputForm: false,
    tabletBreakpoint: 768,
    desktopBreakpoint: 992,
  });
  const { openDialog, closeDialog } = useContext(DialogContext);
  const { isTablet, isDesktop } = useMatchMedia({
    tabletBreakpoint: customConfig.tabletBreakpoint,
    desktopBreakpoint: customConfig.desktopBreakpoint,
  });
  const [triggeredValidate, setTriggeredValidate] = useState(false);
  const [isVirtual, setIsVirtual] = useState(false);

  const referral = useMemo(() => {
    if (searchParams.has('referral')) {
      return searchParams.get('referral');
    }
    return null;
  }, [searchParams]);

  const validate = useCallback(() => {
    setTriggeredValidate(true);
    let valid = true;
    if (attendanceType.trim() === '') {
      valid = false;
      setAttendanceTypeError(t('registration.error-attendance-type'));
    } else {
      setAttendanceTypeError(null);
    }
    if (title.trim() === '') {
      valid = false;
      setTitleError(t('registration.error-title'));
    } else {
      setTitleError(null);
    }
    if (email.trim() === '') {
      valid = false;
      setEmailError(t('registration.error-email'));
    } else {
      setEmailError(null);
    }
    if (firstName.trim() === '') {
      valid = false;
      setFirstNameError(t('registration.error-first-name'));
    } else {
      setFirstNameError(null);
    }
    if (lastName.trim() === '') {
      valid = false;
      setLastNameError(t('registration.error-last-name'));
    } else {
      setLastNameError(null);
    }
    if (company.trim() === '') {
      valid = false;
      setCompanyError(t('registration.error-company'));
    } else {
      setCompanyError(null);
    }
    if (countryRegion.trim() === '') {
      valid = false;
      setCountryRegionError(t('registration.error-jurisdiction'));
    } else {
      setCountryRegionError(null);
    }
    if (!acceptPics) {
      valid = false;
      setAcceptPicsError(t('registration.error-agree-pics'));
    } else {
      setAcceptPicsError(null);
    }
    if (!acceptPhoto) {
      valid = false;
      setAcceptPhotoError(t('registration.error-agree-photo'));
    } else {
      setAcceptPhotoError(null);
    }
    if (!acceptTerms) {
      valid = false;
      setAcceptTermsError(t('registration.error-agree-terms'));
    } else {
      setAcceptTermsError(null);
    }

    setSubmittable(valid);
    return valid;
  }, [
    acceptPhoto,
    acceptPics,
    acceptTerms,
    attendanceType,
    company,
    countryRegion,
    email,
    firstName,
    lastName,
    t,
    title,
  ]);

  const passcodeSubmitHandler = (e) => {
    if (e) {
      e.preventDefault();
    }
    if (loading) {
      return;
    }
    setLoading(true);
    let valid = validate();
    if (!valid) {
      setLoading(false);
      setTimeout(() => {
        document
          .getElementsByClassName('input-row error')[0]
          .scrollIntoView({ behavior: 'smooth' });
      }, 100);
    } else {
      const data = {
        email: email.trim(),
      };
      const selectedJurisdiction =
        CHINA_GROUP.options.find((country) => country.value === countryRegion) ||
        COUNTRIES.find((country) => country.value === countryRegion);
      const metadata = {
        attendanceType: attendanceType,
        title: title,
        firstName: firstName.trim(),
        lastName: lastName.trim(),
        name: `${title} ${firstName.trim()} ${lastName.trim()}`,
        jobTitle: jobTitle.trim(),
        company: company.trim(),
        jurisdiction: countryRegion,
        jurisdictionDisplay: selectedJurisdiction?.en || countryRegion,
        acceptPics,
        acceptPhoto,
        acceptTerms,
        acceptMarketing,
        locale: i18n.language,
      };
      if (referral) {
        metadata.referral = referral;
      }
      data.metadata = metadata;

      API.registration(eventId, data)
        .then(() => {
          setLoading(false);
          openDialog(t('registration.submit-succeed'), () => {
            closeDialog();
            // navigate(`/event/${eventId}`);
            window.location.reload();
          });
        })
        .catch((err) => {
          setLoading(false);
          if (err && err.response && err.response.status === 409) {
            openDialog(t('registration.submit-failed-email'), closeDialog);
          } else {
            openDialog(t('registration.submit-failed'), closeDialog);
          }
        });
    }
  };

  /*
  useEffect(() => {
    if (referral === 'ey' || referral === 'womenasso') {
      setCustomConfig((customConfig) => ({
        ...customConfig,
        hideInputForm: false,
      }));
    }
  }, [referral]);
  */

  useEffect(() => {
    if (customization.passcode) {
      if (customization.passcode.style) {
        let customStyle = {};
        for (const [key, value] of Object.entries(customization.passcode.style)) {
          customStyle[key] = value;
        }
        setCustomStyle(customStyle);
      }
      if (customization.passcode.config) {
        setCustomConfig((customConfig) => ({
          ...customConfig,
          ...customization.passcode.config,
        }));
      }
    }
    if (customization.virtual === true) {
      setIsVirtual(true);
    }
  }, [customization]);

  useEffect(() => {
    if (triggeredValidate) {
      validate();
    }
  }, [triggeredValidate, validate]);

  const emailChangeHandler = (e) => {
    setEmail(e.target.value);
  };

  const countryRegionChangeHandler = (e) => {
    setCountryRegion(e.target.value);
  };
  const titleChangeHandler = (e) => {
    setTitle(e.target.value);
  };

  const firstNameChangeHandler = (e) => {
    setFirstName(e.target.value);
  };

  const lastNameChangeHandler = (e) => {
    setLastName(e.target.value);
  };

  const jobTitleChangeHandler = (e) => {
    setJobTitle(e.target.value);
  };

  const companyChangeHandler = (e) => {
    setCompany(e.target.value);
  };

  const attendanceTypeChangeHandler = (e) => {
    setAttendanceType(e.target.value);
  };

  return (
    <div
      className={`registration-page${isTablet ? ' tablet' : ''}${isDesktop ? ' desktop' : ''}`}
      style={customStyle}
    >
      <div className="background-wrapper">
        <div className="banner">
          <img src={webBanner} alt="banner"></img>
        </div>
        <div className="header">
          {customConfig.enableLocale && (
            <LocaleSelector availableLocales={customConfig.availableLocales} />
          )}
        </div>
        <div className="content">
          {customConfig.mobileBanner && (
            <div className="mobile-banner">
              <img src={customConfig.mobileBanner} alt="banner"></img>
            </div>
          )}
          <div className="content-container">
            {customConfig.enableLocale && (
              <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
                <LocaleSelector availableLocales={customConfig.availableLocales} />
              </div>
            )}
            {/*<div className="logo-wrapper-mobile">
              {customConfig.logoImageUrl ? (
                <React.Fragment>
                  {customConfig.logoImageUrlMobile ? (
                    <img src={customConfig.logoImageUrlMobile} className="logo" alt="logo" />
                  ) : (
                    <img src={customConfig.logoImageUrl} className="logo" alt="logo" />
                  )}
                </React.Fragment>
              ) : (
                <img src={logoEn} className="logo" alt="logo" />
              )}
            </div>
            <div className="logo-wrapper">
              {customConfig.logoImageUrl ? (
                <img src={customConfig.logoImageUrl} className="logo" alt="logo" />
              ) : (
                <img src={logoEn2x} className="logo" alt="logo" />
              )}
            </div>*/}
            <div className="event-details">
              <div className="event-logo">
                <img src={eventLogo} alt="IOMed Global Series" />
              </div>
              {customConfig.title !== null ? (
                <div
                  className="event-heading"
                  dangerouslySetInnerHTML={{
                    __html: getMultiLocaleContent(customConfig.title, i18n.language),
                  }}
                ></div>
              ) : (
                <div className="event-heading">{t('registration.title')}</div>
              )}
              <div className="event-intro">
                <p>{t('registration.intro-1')}</p>
                <p>{t('registration.intro-2')}</p>
              </div>
              {isVirtual ? (
                <div className="event-info">
                  <div className="detail">
                    <div className="heading">{t('registration.date-key')}:</div>
                    <div className="value">{t('registration.virtual-date')}</div>
                  </div>
                  <div className="detail">
                    <div className="heading">{t('registration.time-key')}:</div>
                    <div className="value">{t('registration.virtual-time')}</div>
                  </div>
                  <div className="detail">
                    <div className="heading">{t('registration.format-key')}:</div>
                    <div className="value">{t('registration.virtual-format')}</div>
                  </div>
                </div>
              ) : (
                <div className="event-info">
                  <div className="section-title">{t('registration.event-details')}</div>
                  <div className="details-list">
                    <div className="detail">
                      <div className="heading">{t('registration.date-key')}:</div>
                      <div className="value">{t('registration.date')}</div>
                    </div>
                    <div className="detail">
                      <div className="heading">{t('registration.time-key')}:</div>
                      <div className="value">{t('registration.time')}</div>
                    </div>
                    <div className="detail">
                      <div className="heading">{t('registration.venue-key')}:</div>
                      <div className="value">
                        <div>{t('registration.venue')}</div>
                        <ul className="address-list">
                          <li>{t('registration.address')}</li>
                          <li>{t('registration.address-2')}</li>
                        </ul>
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </div>
            {!customConfig.hideInputForm && (
              <form onSubmit={passcodeSubmitHandler}>
                <div className={`input-container`}>
                  <div className="form-section-title">{t('registration.form-heading')}</div>
                  <div className={`input-row${attendanceTypeError ? ' error' : ''}`}>
                    <label className="input-label" htmlFor="attendance-type">
                      {t('registration.form-attendance-type')}*:
                    </label>
                    <div className="select-wrapper">
                      <select
                        value={attendanceType}
                        name="attendance-type"
                        id="attendance-type"
                        onChange={attendanceTypeChangeHandler}
                        disabled
                      >
                        <option value="">{t('registration.please-select')}</option>
                        <option value="in-person">{t('registration.attendance-physical')}</option>
                        <option value="online">{t('registration.attendance-online')}</option>
                      </select>
                    </div>
                    <div className="error">{attendanceTypeError}</div>
                  </div>
                  <div className={`input-row${titleError ? ' error' : ''}`}>
                    <label className="input-label" htmlFor="title">
                      {t('registration.form-title')}*:
                    </label>
                    <div className="select-wrapper">
                      <select value={title} name="title" id="title" onChange={titleChangeHandler}>
                        <option value="">{t('registration.please-select')}</option>
                        <option value="Prof">{t('registration.title-prof')}</option>
                        <option value="Dr">{t('registration.title-dr')}</option>
                        <option value="Mr">{t('registration.title-mr')}</option>
                        <option value="Mrs">{t('registration.title-mrs')}</option>
                        <option value="Ms">{t('registration.title-ms')}</option>
                        <option value="Miss">{t('registration.title-miss')}</option>
                        <option value="Others">{t('registration.title-others')}</option>
                      </select>
                    </div>
                    <div className="error">{titleError}</div>
                  </div>
                  <div className={`input-row${firstNameError ? ' error' : ''}`}>
                    <label className="input-label" htmlFor="firstName">
                      {t('registration.form-first-name')}*:
                    </label>
                    <input
                      value={firstName}
                      type="text"
                      name="firstName"
                      id="firstName"
                      maxLength={50}
                      onChange={firstNameChangeHandler}
                    />
                    <div className="error">{firstNameError}</div>
                  </div>
                  <div className={`input-row${lastNameError ? ' error' : ''}`}>
                    <label className="input-label" htmlFor="lastName">
                      {t('registration.form-last-name')}*:
                    </label>
                    <input
                      value={lastName}
                      type="text"
                      name="lastName"
                      id="lastName"
                      maxLength={50}
                      onChange={lastNameChangeHandler}
                    />
                    <div className="error">{lastNameError}</div>
                  </div>
                  <div className={`input-row${emailError ? ' error' : ''}`}>
                    <label className="input-label" htmlFor="email">
                      {t('registration.form-email')}*:
                    </label>
                    <input
                      value={email}
                      type="email"
                      name="email"
                      id="email"
                      pattern="^[a-zA-Z0-9.!#$%&'*+\/=?^_`\{\|\}~\-]+@[a-zA-Z0-9](?:[a-zA-Z0-9\-]{0,61}[a-zA-Z0-9])?(?:\.[a-zA-Z0-9](?:[a-zA-Z0-9\-]{0,61}[a-zA-Z0-9])?)+$"
                      onChange={emailChangeHandler}
                    />
                    <div className="error">{emailError}</div>
                  </div>
                  <div className={`input-row${companyError ? ' error' : ''}`}>
                    <label className="input-label" htmlFor="company">
                      {t('registration.form-company')}*:
                    </label>
                    <input
                      value={company}
                      type="text"
                      name="company"
                      id="company"
                      maxLength={100}
                      onChange={companyChangeHandler}
                    />
                    <div className="error">{companyError}</div>
                  </div>
                  <div className="input-row">
                    <label className="input-label" htmlFor="jobTitle">
                      {t('registration.form-job-title')}:
                    </label>
                    <input
                      value={jobTitle}
                      type="text"
                      name="jobTitle"
                      id="jobTitle"
                      maxLength={100}
                      onChange={jobTitleChangeHandler}
                    />
                  </div>
                  <div className={`input-row${countryRegionError ? ' error' : ''}`}>
                    <label className="input-label" htmlFor="jurisdiction">
                      {t('registration.form-jurisdiction')}*:
                    </label>
                    <div className="select-wrapper">
                      <select
                        value={countryRegion}
                        name="jurisdiction"
                        id="jurisdiction"
                        onChange={countryRegionChangeHandler}
                      >
                        <option value="">{t('registration.please-select')}</option>
                        <optgroup label={CHINA_GROUP[i18n.language] || CHINA_GROUP.en}>
                          {CHINA_GROUP.options.map((country) => (
                            <option key={country.value} value={country.value}>
                              {`\u00A0-\u00A0${country[i18n.language] || country.en}`}
                            </option>
                          ))}
                        </optgroup>
                        <option disabled>----------</option>
                        {COUNTRIES.map((country) => (
                          <option key={country.value} value={country.value}>
                            {country[i18n.language] || country.en}
                          </option>
                        ))}
                      </select>
                    </div>
                    <div className="error">{countryRegionError}</div>
                  </div>
                  <div className="agreement-list">
                    <div className={`input-row${acceptPicsError ? ' error' : ''}`}>
                      <div className="checkbox-container">
                        <input
                          type="checkbox"
                          name="accept-pics"
                          id="accept-pics"
                          checked={acceptPics}
                          onChange={(e) => setAcceptPics(e.target.checked)}
                        />
                        <label className="input-label" htmlFor="accept-pics">
                          <Trans i18nKey="registration.form-agree-pics">
                            <a
                              href={t('registration.pics-url')}
                              target="_blank"
                              rel="noreferrer"
                              onClick={(e) => e.stopPropagation()}
                            >
                              Personal Information Collection Statement
                            </a>
                          </Trans>
                        </label>
                      </div>
                      <div className="error">{acceptPicsError}</div>
                    </div>
                    <div className={`input-row${acceptPhotoError ? ' error' : ''}`}>
                      <div className="checkbox-container">
                        <input
                          type="checkbox"
                          name="accept-photo"
                          id="accept-photo"
                          checked={acceptPhoto}
                          onChange={(e) => setAcceptPhoto(e.target.checked)}
                        />
                        <label className="input-label" htmlFor="accept-photo">
                          <Trans i18nKey="registration.form-agree-photo">
                            <a
                              href={t('registration.photo-url')}
                              target="_blank"
                              rel="noreferrer"
                              onClick={(e) => e.stopPropagation()}
                            >
                              Statement on Use of Event Photos and/or Videos
                            </a>
                          </Trans>
                        </label>
                      </div>
                      <div className="error">{acceptPhotoError}</div>
                    </div>
                    <div className={`input-row${acceptTermsError ? ' error' : ''}`}>
                      <div className="checkbox-container">
                        <input
                          type="checkbox"
                          name="accept-terms"
                          id="accept-terms"
                          checked={acceptTerms}
                          onChange={(e) => setAcceptTerms(e.target.checked)}
                        />
                        <label className="input-label" htmlFor="accept-terms">
                          <Trans i18nKey="registration.form-agree-terms">
                            <a
                              href={t('registration.terms-url')}
                              target="_blank"
                              rel="noreferrer"
                              onClick={(e) => e.stopPropagation()}
                            >
                              Terms and Conditions
                            </a>
                          </Trans>
                        </label>
                      </div>
                      <div className="error">{acceptTermsError}</div>
                    </div>
                    <div className="input-row">
                      <div className="checkbox-container">
                        <input
                          type="checkbox"
                          name="accept-marketing"
                          id="accept-marketing"
                          checked={acceptMarketing}
                          onChange={(e) => setAcceptMarketing(e.target.checked)}
                        />
                        <label className="input-label" htmlFor="accept-marketing">
                          {t('registration.form-agree-marketing')}
                        </label>
                      </div>
                    </div>
                  </div>
                  <button
                    type="submit"
                    className={`submit-btn${submittable ? ' active' : ''}`}
                    disabled={!submittable}
                  >
                    {loading ? (
                      <CircularProgress size={21} />
                    ) : (
                      <span>{t('registration.submit')}</span>
                    )}
                  </button>
                </div>
              </form>
            )}
            {customConfig.hideInputForm && (
              <Fragment>
                {customConfig.remarks.length > 0 && (
                  <div className="remarks">
                    {customConfig.remarks.map((remark, index) => {
                      return (
                        <div
                          className="remark"
                          key={index}
                          dangerouslySetInnerHTML={{
                            __html: getMultiLocaleContent(remark, i18n.language),
                          }}
                        ></div>
                      );
                    })}
                  </div>
                )}
              </Fragment>
            )}
            {/*<div className="remarks">
              <div className="remark">
                Supported by Pilot Scheme for Characteristic Local Tourism Events. Any opinions or
                recommendations expressed in this materials or any activities organised under this
                event do not reflect the views of the Government of the Hong Kong Special
                Administrative Region, the Hong Kong Tourism Board, or the Evaluation Panel of the
                Pilot Scheme for Characteristic Local Tourism Events.
                <br />
                本地特色旅遊活動先導計劃支持活動。在此刊物或與活動有關的任何活動內表達之任何意見或建議，並不代表香港特別行政區政府、香港旅遊發展局或本地特色旅遊活動先導計劃評審委員會的觀點。
              </div>
            </div>*/}
          </div>
          {/*<div className="mobile-padding"></div>*/}
          {/*<div className="padding"></div>*/}
          {customConfig.mobileBottomBanner && (
            <div className="mobile-bottom-banner">
              <img src={customConfig.mobileBottomBanner} alt="banner"></img>
            </div>
          )}
        </div>
        <div className="footer">
          <div className="footer-main">
            <div className="copyright">{t('registration.copyright')}</div>
            {!customConfig.hideFooter && (
              <React.Fragment>
                {customConfig.footerImageUrl || customConfig.footerText ? (
                  <a href={customConfig.footerLink} className="link">
                    {customConfig.footerImageUrl && (
                      <img className="logo" src={customConfig.footerImageUrl} alt="Logo"></img>
                    )}
                    {customConfig.footerText && (
                      <span className="text">
                        {getMultiLocaleContent(customConfig.footerText, i18n.language)}
                      </span>
                    )}
                  </a>
                ) : customConfig.hideFooterLink ? (
                  <div className="link">
                    <img className="logo" src={logo} alt="OCGL Logo"></img>
                    <span className="text">{t('app.footer')}</span>
                  </div>
                ) : (
                  <a href="https://www.oneclickgolive.com" className="link">
                    <img className="logo" src={logo} alt="Logo"></img>
                    <span className="text">{t('app.footer')}</span>
                  </a>
                )}
              </React.Fragment>
            )}
          </div>
          {process.env.REACT_APP_IS_CN === '1' && (
            <div className="icp">
              <a href="http://beian.miit.gov.cn/" rel="noreferrer" target="_blank">
                京ICP备16066699号-2
              </a>
              <a
                href="http://www.beian.gov.cn/portal/registerSystemInfo?recordcode=11010502051708"
                rel="noreferrer"
                target="_blank"
              >
                <img src={beianLogo} style={{ height: '20px', width: '20px' }} alt="beianLogo" />
                京公网安备 11010502051708号
              </a>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
